import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, LayoutList, LineChart, Table } from 'lucide-react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useTheme } from '@mui/material';

import { ReactComponent as BestBiologyIcon } from '../../assets/best-biology-plan-icon.svg';
import { ReactComponent as BestEconomyIcon } from '../../assets/best-economy-plan-icon.svg';
import { ReactComponent as BestEnvironmentIcon } from '../../assets/best-environment-plan-icon.svg';

import IconTabs from 'components/icon-tabs';
import LoadingSpinner from 'components/loading-spinner';
import CompareTable from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/compare-table';
import ScenarioResultGraphTab from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/result-sites/scenario-result-graph-tab';
import ScenarioFeedList from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/scenario-feed-list';
import TopPerformanceCards from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/top-performance-cards';
import ScenarioBottomCompareBar from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/compare-clone-bar';
import { Summary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { ScenarioHarvestTargetType } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { ScenarioDefaults } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario.type';
import {
  getPercentageDifference,
  getRankedSummaries,
  getScenarioGrowthRate,
} from 'views/bluviu-nutrition/features/scenarios-v2/utils/compare-result-utils';
import { FormattedMessage, FormattedNumber } from 'react-intl';

type TopPerformerItem = {
  title: React.ReactNode;
  text: React.ReactNode;
  icon: React.ReactNode;
  planName: string;
};

const PercentageValue: React.FC<{ value: number }> = ({ value }) => {
  if (value > 0 && value < 0.1) {
    return <FormattedMessage id="bluViuNutrition.viewScenarioResultGt2.fixedNumber" />;
  }

  return <FormattedNumber value={value} minimumFractionDigits={1} maximumFractionDigits={1} />;
};

const buildTopPerformerItems = (summaries: Summary[], scenarioDefaults?: ScenarioDefaults): TopPerformerItem[] => {
  const lowestCo2 = getRankedSummaries(summaries, (summary) => summary.globalWarmingPotentialCo2KgPerKgGrowth, 'asc');
  const fastestGrowth = getRankedSummaries(summaries, getScenarioGrowthRate, 'desc');
  const lowestFeedPrice = getRankedSummaries(summaries, (summary) => summary.averageFeedCostPerKgFeed, 'asc');
  const normalizedHarvestTargetType = scenarioDefaults?.harvestTargetType?.toLowerCase();
  const isWeightTarget = normalizedHarvestTargetType === ScenarioHarvestTargetType.WEIGHT.toLowerCase();

  const items: TopPerformerItem[] = [];

  if (lowestCo2.length >= 2) {
    items.push({
      title: <FormattedMessage id="bluViuNutrition.viewScenarioResultGt2.icons.lowestCo2Title" />,
      text: (
        <FormattedMessage
          id="bluViuNutrition.viewScenarioResultGt2.lowestCo2Feed"
          values={{ percentage: <PercentageValue value={getPercentageDifference(lowestCo2[0].value, lowestCo2[1].value)} /> }}
        />
      ),
      icon: <BestEnvironmentIcon />,
      planName: lowestCo2[0].summary.feedPlan,
    });
  }

  if (fastestGrowth.length >= 2) {
    const daysDifference = fastestGrowth[1].summary.daysToReach - fastestGrowth[0].summary.daysToReach;
    const shouldShowDaysDifference = isWeightTarget && daysDifference > 0;

    items.push({
      title: <FormattedMessage id="bluViuNutrition.viewScenarioResultGt2.icons.bestBioTitle" />,
      text: shouldShowDaysDifference ? (
        <FormattedMessage id="bluViuNutrition.viewScenarioResultGt2.bestBiologyFeedWeight" values={{ daysToReach: daysDifference }} />
      ) : (
        <FormattedMessage
          id="bluViuNutrition.viewScenarioResultGt2.bestBiologyFeedWeek"
          values={{
            percentage: (
              <PercentageValue
                value={getPercentageDifference(fastestGrowth[0].summary.weightGainGrams, fastestGrowth[1].summary.weightGainGrams)}
              />
            ),
            week: scenarioDefaults?.harvestTarget,
          }}
        />
      ),
      icon: <BestBiologyIcon />,
      planName: fastestGrowth[0].summary.feedPlan,
    });
  }

  if (lowestFeedPrice.length >= 2) {
    items.push({
      title: <FormattedMessage id="bluViuNutrition.viewScenarioResultGt2.icons.mostEconomicalTitle" />,
      text: (
        <FormattedMessage
          id="bluViuNutrition.viewScenarioResultGt2.mostEconomicalFeed"
          values={{
            percentage: <PercentageValue value={getPercentageDifference(lowestFeedPrice[0].value, lowestFeedPrice[1].value)} />,
          }}
        />
      ),
      icon: <BestEconomyIcon />,
      planName: lowestFeedPrice[0].summary.feedPlan,
    });
  }

  return items;
};

const ResultTabHeader: React.FC = () => {
  const {
    getScenarioPostProcessQuery: { data: postProcessData, isLoading },
    setSelectedPrograms,
  } = useScenarioResultState();
  const { siteSummary = [], scenarioDefaults } = postProcessData ?? {};
  const theme = useTheme();
  const [selectedFeedPlans, setSelectedFeedPlans] = useState<Summary[]>([]);
  const [compareKey, setCompareKey] = useState(0);

  const siteTabs = [
    {
      label: 'Overview',
      icon: <LayoutList size={20} />,
      value: 'overview',
      tabContent: <ScenarioFeedList selectedFeedPlans={selectedFeedPlans} setSelectedFeedPlans={setSelectedFeedPlans} />,
    },
    { label: 'Compare', icon: <Table size={20} />, value: 'compare', tabContent: <CompareTable key={compareKey} /> },
    {
      label: 'Graph',
      icon: <LineChart size={20} />,
      value: 'graph',
      tabContent: <ScenarioResultGraphTab />,
    },
  ];

  const [selectedTab, setSelectedTab] = useState<string | number>('overview');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string | number) => {
    if (newValue === 'overview') {
      setSelectedFeedPlans([]);
      setSelectedPrograms([]);
      setCompareKey((prev) => prev + 1);
    }
    setSelectedTab(newValue);
  };

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const toggleLabel = isExpanded ? 'Hide' : 'Show';
  const Icon = isExpanded ? ChevronUp : ChevronDown;

  const handleExpansion = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setIsExpanded(isExpanded);
  };

  const items = useMemo(() => buildTopPerformerItems(siteSummary, scenarioDefaults), [siteSummary, scenarioDefaults]);

  return (
    <>
      <Accordion disableGutters expanded={isExpanded} onChange={handleExpansion} sx={{ background: 'transparent', p: 0 }}>
        <AccordionSummary aria-controls="panel1-content" id="panel1-header">
          <Box display={'flex'} flexDirection={'row'} textAlign={'center'} gap={2} alignItems={'center'}>
            <Typography variant="h6" fontWeight={600} color={theme.palette.common.white}>
              Top Performers (avg.)
            </Typography>
            <Box display={'flex'} gap={2}>
              <Typography color={theme.palette.common.white} variant="body1">
                {toggleLabel}
              </Typography>
              <Icon color={theme.palette.common.white} />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TopPerformanceCards items={items} />
        </AccordionDetails>
      </Accordion>

      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Box display={'flex'} alignItems="center" justifyContent="space-between">
          <Typography variant="h5" color={'white'} fontWeight={600}>
            Feed programs
          </Typography>
          <IconTabs tabs={siteTabs} value={selectedTab} onChange={handleTabChange} />
        </Box>

        <Box display={'flex'} flexDirection={'column'} maxWidth={'100%'}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            siteTabs.map((tab) => (
              <Box
                key={tab.value}
                hidden={selectedTab !== tab.value} // Hide inactive tabs but don't unmount them
                sx={{
                  display: selectedTab === tab.value ? 'block' : 'none', // Only display the active tab
                }}>
                {tab.tabContent}
              </Box>
            ))
          )}
        </Box>
      </Box>

      <ScenarioBottomCompareBar
        selectedFeedPlans={selectedFeedPlans}
        showCompareBottomBar={selectedFeedPlans.length > 1 && selectedTab === 'overview'}
        tabChange={() => setSelectedTab('compare')}
        clearSelection={() => {
          setSelectedFeedPlans([]);
          setSelectedPrograms([]);
        }}
      />
    </>
  );
};

export default React.memo(ResultTabHeader);
